/**
 * Shade Instagram feed for the "Tested in the field" gallery.
 *
 * Progressive enhancement. The gallery's five <img> frames render server-side
 * with the static shade-field-0N.jpg placeholders, which stay as the fallback
 * if anything below fails. On connect this fetches the brand's latest posts
 * from a Behold JSON feed (https://feeds.behold.so/<feedId>) and swaps each
 * frame's image, newest first, left to right, then links the frame to the
 * Instagram post. The desktop scrub (#initGalleryScrub) and the mobile carousel
 * (#initMobileCarousels) in shade-reveal.js iterate the FRAME elements, not the
 * images, so both keep working untouched after the swap.
 *
 * Resilience: no feed id, fetch error, non-OK response, malformed JSON, or
 * fewer posts than frames -> the static placeholders stay. The gallery never
 * blanks. Swapping images is content, not motion, so it runs for everyone
 * including prefers-reduced-motion users (no animation is introduced here).
 *
 * Self-instantiates one instance on document.body (mirrors shade-reveal.js), so
 * no Liquid or section edit is needed. Idempotent and theme-editor safe: it
 * re-runs the swap on shopify:section:load when the gallery is present, and
 * never double-wraps a frame that already holds an Instagram link.
 */

// Behold feed id. Paste the id from the feed's Behold dashboard (the last path
// segment of https://feeds.behold.so/<feedId>). Empty string = no-op: the
// static field photos render and nothing is fetched.
const FEED_ID = 'skPcKeFtFDKc7j135JlG';

const FEED_BASE = 'https://feeds.behold.so/';
const GALLERY_SELECTOR = "[id$='__gallery'] .group-block .group-block-content";

class ShadeInstagram extends HTMLElement {
  /** @type {boolean} Set once a usable feed has populated the frames. */
  #done = false;

  connectedCallback() {
    if (!FEED_ID) return;
    this.#hydrate();
    document.addEventListener('shopify:section:load', this.#onSectionLoad);
  }

  disconnectedCallback() {
    document.removeEventListener('shopify:section:load', this.#onSectionLoad);
  }

  /**
   * Re-hydrate when a section reloads in the theme editor. The gallery may have
   * been re-rendered with fresh placeholder <img>s, so allow one more swap.
   * #hydrate() bails cheaply if the gallery is not on the page.
   */
  #onSectionLoad = () => {
    this.#done = false;
    this.#hydrate();
  };

  async #hydrate() {
    if (this.#done) return;

    const row = document.querySelector(GALLERY_SELECTOR);
    if (!row) return;

    const frames = /** @type {HTMLElement[]} */ (
      Array.from(row.children).filter((el) => el.querySelector('img'))
    );
    if (frames.length === 0) return;

    let data;
    try {
      const res = await fetch(FEED_BASE + FEED_ID, { headers: { Accept: 'application/json' } });
      if (!res.ok) return;
      data = await res.json();
    } catch (_error) {
      return; // network or parse failure -> keep the static placeholders
    }

    const posts = Array.isArray(data && data.posts) ? data.posts : [];
    if (posts.length === 0) return;

    // A usable feed arrived. Mark done so spurious editor section-loads on other
    // sections do not refetch.
    this.#done = true;

    frames.forEach((frame, i) => {
      const post = posts[i];
      if (post) this.#applyPost(frame, post);
      // Fewer posts than frames: leave the remaining placeholders in place.
    });
  }

  /**
   * Swap one frame's image to the Instagram post and link it to the permalink.
   * Mutates the existing <img> in place (keeps layout, scrub, and carousel
   * bindings intact) and wraps it once in an <a> that fills the frame.
   * @param {HTMLElement} frame
   * @param {Record<string, any>} post
   */
  #applyPost(frame, post) {
    const img = /** @type {HTMLImageElement | null} */ (frame.querySelector('img'));
    if (!img) return;

    const src = this.#pickSrc(post);
    if (!src) return;

    // Build a srcset from Behold's resized variants so the browser picks the
    // right one for the frame width (≈20vw desktop, ≈78vw mobile).
    img.removeAttribute('srcset');
    img.removeAttribute('sizes');
    const srcset = this.#buildSrcset(post.sizes);
    if (srcset) {
      img.srcset = srcset;
      img.sizes = '(min-width: 750px) 20vw, 78vw';
    }
    img.src = src;
    img.loading = 'lazy';
    img.decoding = 'async';

    const altRaw = (post.altText || post.prunedCaption || 'Instagram post from Shade Studios')
      .replace(/\s+/g, ' ')
      .trim();
    img.alt = altRaw.length > 125 ? `${altRaw.slice(0, 122)}…` : altRaw;

    if (!post.permalink) return;

    // Link the frame to the Instagram post. Wrap the <img> once; on a re-run
    // reuse the existing link rather than nesting a second one.
    let link = /** @type {HTMLAnchorElement | null} */ (frame.querySelector('a.shade-ig-link'));
    if (!link) {
      link = document.createElement('a');
      link.className = 'shade-ig-link';
      link.target = '_blank';
      link.rel = 'noopener noreferrer';
      const parent = img.parentNode;
      if (parent) {
        parent.insertBefore(link, img);
        link.appendChild(img);
      }
    }
    link.href = post.permalink;
    link.setAttribute('aria-label', `View on Instagram: ${img.alt}`);
  }

  /**
   * Best display source for a post. Prefer Behold's re-hosted resized variants
   * (stable URLs) over the raw mediaUrl/thumbnailUrl, which point at the
   * Instagram CDN and can expire.
   * @param {Record<string, any>} post
   * @returns {string}
   */
  #pickSrc(post) {
    const s = post.sizes || {};
    return (
      (s.medium && s.medium.mediaUrl) ||
      (s.large && s.large.mediaUrl) ||
      (s.full && s.full.mediaUrl) ||
      (s.small && s.small.mediaUrl) ||
      post.thumbnailUrl ||
      post.mediaUrl ||
      ''
    );
  }

  /**
   * @param {Record<string, {mediaUrl?: string, width?: number}> | undefined} sizes
   * @returns {string}
   */
  #buildSrcset(sizes) {
    if (!sizes) return '';
    const parts = [];
    for (const key of ['small', 'medium', 'large', 'full']) {
      const variant = sizes[key];
      if (variant && variant.mediaUrl && variant.width) {
        parts.push(`${variant.mediaUrl} ${variant.width}w`);
      }
    }
    return parts.join(', ');
  }
}

// Register the custom element once.
if (!customElements.get('shade-instagram')) {
  customElements.define('shade-instagram', ShadeInstagram);
}

// Self-instantiate exactly one instance, only when a feed id is configured.
// Idempotent: never inject a second tag.
if (FEED_ID && !document.querySelector('shade-instagram')) {
  document.body.appendChild(document.createElement('shade-instagram'));
}
